import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Bug, ChevronDown, ChevronRight } from 'lucide-react'
import { isDevMode } from '../lib/devMode'
import { isPlatformStaff } from '../lib/roleHelpers'

// DevScanPanel — collapsed-by-default panel that lets the GM/tester
// simulate scans without printing physical QR codes. Only renders when
// isDevMode is true. Selecting an item builds the canonical QR string
// for that scan type and feeds it to QRScanner via simulatedQrText on
// the parent, so the same handler path runs end-to-end.
//
// Track 7.3 + Bug 46 — refresh post-FRAGO-rework:
//   - Intel scan option REMOVED. Intel triggers are absorbed into the
//     FRAGO activation-trigger system; ANUBIS-INTEL-{id} stays parseable
//     in the router for legacy events but new content shouldn't generate
//     intel-trigger rows.
//   - FRAGO trigger scan option ADDED. Picks a queued FRAGO with
//     activation_trigger_type='qr_discovery' and fires ANUBIS-FRAGO-{id}.
//   - Player kill scan now generates the bare-UUID canonical form (used
//     to be deathcard://claim/{uuid}; parser still accepts the legacy
//     form for old printed patches but new generation drops it).
//   - The Player Scan flow (Amendment #1 contact) reuses the same kill
//     scan — the FRAGO engine intercepts when there's an active
//     player_scan objective targeting the scanned player. So this panel
//     surfaces ONE "scan player" action, and routes through the same
//     priority logic the real scanner applies.
//
// Props:
//   activeEventId   — required; panel hides when null
//   onSimulate(text) — called with the synthetic QR string. The parent
//                      should pass that string through to QRScanner.
export default function DevScanPanel({ activeEventId, onSimulate, profile }) {
  // UX-3 — gate ships to prod under platform-staff. Dev mode still
  // exposes the panel to local devs running the unauthenticated mock,
  // but the real visibility lever is `isPlatformStaff` on the viewer's
  // profile. Removed entirely before public launch.
  const isAllowed = isDevMode || isPlatformStaff(profile)

  const [open, setOpen] = useState(false)
  const [players, setPlayers] = useState([])
  const [equipment, setEquipment] = useState([])
  const [checkpoints, setCheckpoints] = useState([])
  const [fragoTriggers, setFragoTriggers] = useState([])
  const [selectedPlayer, setSelectedPlayer] = useState('')
  const [selectedEquip, setSelectedEquip] = useState('')
  const [selectedCp, setSelectedCp] = useState('')
  const [selectedFrago, setSelectedFrago] = useState('')

  useEffect(() => {
    if (!isAllowed || !activeEventId || !open) return
    fetchOptions()
  }, [activeEventId, open, isAllowed])

  const fetchOptions = async () => {
    const [plyRes, eqRes, cpRes, fragoRes] = await Promise.all([
      supabase.from('event_players')
        .select('user_id, users(callsign, display_name, email)')
        .eq('event_id', activeEventId),
      supabase.from('event_equipment')
        .select('id, name, status').eq('event_id', activeEventId)
        .order('created_at', { ascending: false }),
      supabase.from('event_checkpoints')
        .select('id, name, status').eq('event_id', activeEventId)
        .order('created_at', { ascending: false }),
      // FRAGOs with QR-discovery activation that haven't been triggered yet.
      supabase.from('frago_trigger_qr')
        .select('id, qr_code, scanned, frago_id, frago:bounties!frago_trigger_qr_frago_id_fkey(mission_name, status)')
        .eq('event_id', activeEventId).eq('scanned', false),
    ])
    setPlayers(plyRes.data || [])
    setEquipment(eqRes.data || [])
    setCheckpoints(cpRes.data || [])
    setFragoTriggers(fragoRes.data || [])
  }

  if (!isAllowed || !activeEventId) return null

  const firePlayer = () => {
    if (!selectedPlayer) return
    // Bare-UUID kill scan. The FRAGO engine's findPlayerScanObjective
    // hook intercepts this in the scanner if there's a matching active
    // player_scan objective, so this single action covers both kill and
    // contact flows.
    onSimulate(selectedPlayer)
    setSelectedPlayer('')
  }
  const fireEquipment = () => {
    if (!selectedEquip) return
    onSimulate(`ANUBIS-EQUIP-${selectedEquip}`)
    setSelectedEquip('')
  }
  const fireCheckpoint = () => {
    if (!selectedCp) return
    onSimulate(`ANUBIS-CP-${selectedCp}`)
    setSelectedCp('')
  }
  const fireFrago = () => {
    if (!selectedFrago) return
    const row = fragoTriggers.find(f => f.id === selectedFrago)
    if (!row) return
    onSimulate(row.qr_code)
    setSelectedFrago('')
  }

  return (
    <div style={{
      position: 'fixed', bottom: 12, right: 12, zIndex: 60,
      background: 'rgba(28, 38, 38, .95)', border: '1px dashed #ffcc33',
      maxWidth: 320, fontFamily: "'JetBrains Mono', monospace",
      backdropFilter: 'blur(6px)',
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, padding: '8px 12px',
        background: 'rgba(255,204,51,.1)', border: 'none', cursor: 'pointer',
        color: '#ffcc33', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Bug size={12} /> DEV SCAN HELPER
        </span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>

      {open && (
        <div style={{ padding: 12, fontSize: 11 }}>
          <p style={{ color: '#5a6a60', marginBottom: 8 }}>Bypass the camera + simulate scans for the active event.</p>

          {/* Player scan — kill OR contact (player_scan objective)
              depending on what's active. */}
          <div style={{ marginBottom: 10 }}>
            <p style={{ color: '#ffcc33', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
              Simulate Player Scan (kill / contact)
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={selectedPlayer} onChange={e => setSelectedPlayer(e.target.value)}
                style={{ flex: 1, background: '#0a140a', color: '#e0e8de', border: '1px solid rgba(255,204,51,.3)', padding: '4px 8px', fontSize: 11 }}>
                <option value="">— pick a player —</option>
                {players.map(p => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.users?.callsign || p.users?.display_name || p.users?.email}
                  </option>
                ))}
              </select>
              <button onClick={firePlayer} disabled={!selectedPlayer} style={{
                background: '#ffcc33', color: '#000', border: 'none', padding: '4px 10px',
                cursor: selectedPlayer ? 'pointer' : 'not-allowed', opacity: selectedPlayer ? 1 : 0.4,
                fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700,
              }}>FIRE</button>
            </div>
          </div>

          {/* FRAGO trigger scan — fires ANUBIS-FRAGO-{frago_id} so the
              QR router runs handleFragoTriggerScan against a queued
              qr_discovery FRAGO. */}
          <div style={{ marginBottom: 10 }}>
            <p style={{ color: '#ffcc33', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
              Simulate FRAGO Trigger Scan
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={selectedFrago} onChange={e => setSelectedFrago(e.target.value)}
                style={{ flex: 1, background: '#0a140a', color: '#e0e8de', border: '1px solid rgba(255,204,51,.3)', padding: '4px 8px', fontSize: 11 }}>
                <option value="">— pick a QR-trigger FRAGO —</option>
                {fragoTriggers.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.frago?.mission_name || f.qr_code.slice(0, 20)}
                  </option>
                ))}
              </select>
              <button onClick={fireFrago} disabled={!selectedFrago} style={{
                background: '#ffcc33', color: '#000', border: 'none', padding: '4px 10px',
                cursor: selectedFrago ? 'pointer' : 'not-allowed', opacity: selectedFrago ? 1 : 0.4,
                fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700,
              }}>FIRE</button>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <p style={{ color: '#ffcc33', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
              Simulate Equipment Scan
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={selectedEquip} onChange={e => setSelectedEquip(e.target.value)}
                style={{ flex: 1, background: '#0a140a', color: '#e0e8de', border: '1px solid rgba(255,204,51,.3)', padding: '4px 8px', fontSize: 11 }}>
                <option value="">— pick equipment —</option>
                {equipment.map(eq => (
                  <option key={eq.id} value={eq.id}>
                    [{eq.status}] {eq.name}
                  </option>
                ))}
              </select>
              <button onClick={fireEquipment} disabled={!selectedEquip} style={{
                background: '#ffcc33', color: '#000', border: 'none', padding: '4px 10px',
                cursor: selectedEquip ? 'pointer' : 'not-allowed', opacity: selectedEquip ? 1 : 0.4,
                fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700,
              }}>FIRE</button>
            </div>
          </div>

          <div>
            <p style={{ color: '#4dd6ff', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
              Simulate CP Scan
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={selectedCp} onChange={e => setSelectedCp(e.target.value)}
                style={{ flex: 1, background: '#0a140a', color: '#e0e8de', border: '1px solid rgba(77,214,255,.3)', padding: '4px 8px', fontSize: 11 }}>
                <option value="">— pick checkpoint —</option>
                {checkpoints.map(cp => (
                  <option key={cp.id} value={cp.id}>
                    [{cp.status}] {cp.name}
                  </option>
                ))}
              </select>
              <button onClick={fireCheckpoint} disabled={!selectedCp} style={{
                background: '#4dd6ff', color: '#000', border: 'none', padding: '4px 10px',
                cursor: selectedCp ? 'pointer' : 'not-allowed', opacity: selectedCp ? 1 : 0.4,
                fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700,
              }}>FIRE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
